import { useCallback, useEffect, useRef, useState } from 'react';

import { transcribeAudioWithOpenAiProxy } from '~/services/OpenAIProxyService';

export type DictationState = 'idle' | 'recording' | 'transcribing';

export const DICTATION_SUPPORTED =
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';

interface Dictation {
  state: DictationState;
  error: string | null;
  /** The live microphone stream while recording, for `RecordingWaveform`. */
  stream: MediaStream | null;
  toggle: () => void;
}

/**
 * Click-to-dictate for a text field, transcribed by the server-side Whisper proxy so no OpenAI
 * key reaches the browser. The single implementation behind every mic in the admin portal.
 */
export default function useAudioDictation(
  onTranscript: (text: string) => void,
  options: { disabled?: boolean } = {},
): Dictation {
  const { disabled = false } = options;
  const [state, setState] = useState<DictationState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Array<Blob>>([]);
  const onTranscriptRef = useRef(onTranscript);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setStream(null);
  }, []);

  const transcribe = useCallback(
    async (blob: Blob) => {
      setState('transcribing');
      try {
        const text = await transcribeAudioWithOpenAiProxy(blob);
        if (text) {
          onTranscriptRef.current(text);
        }
      } catch {
        setError('Transcription failed. Please try again.');
      } finally {
        setState('idle');
      }
    },
    [setState],
  );

  const start = useCallback(async () => {
    setError(null);
    try {
      const microphone = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = microphone;
      setStream(microphone);
      chunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : undefined;
      const recorder = new MediaRecorder(microphone, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        stopStream();
        void transcribe(blob);
      };

      recorder.start();
      setState('recording');
    } catch (err) {
      stopStream();
      setError(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Microphone access denied. Allow microphone access in your browser settings.'
          : 'Could not access the microphone. Make sure one is connected.',
      );
      setState('idle');
    }
  }, [stopStream, transcribe]);

  const toggle = useCallback(() => {
    if (state === 'recording') {
      recorderRef.current?.stop();
      return;
    }

    if (state === 'idle' && !disabled) {
      void start();
    }
  }, [disabled, start, state]);

  useEffect(() => {
    return () => {
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.onstop = null;
        recorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  return { state, error, stream, toggle };
}
